import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { useAppState } from "../../contexts/App";
import { SiteContextProvider } from "../../contexts/Site";
import { usePosLoginContinue } from "../../hooks/PosLogin";
import { findKeepo, fromLegacyPath } from "../../hooks/SitePath";
import { Site } from "../../model/site";
import { api, ApiSiteResponse } from "../../services/api";
import { LoadingModal } from "../LoadingModal";

type Props = {
  authenticated: boolean;
  /** The Keepo open in the app (if any) */
  site?: Site;
  setSite: (site: Site) => void;
  children: ReactNode;
};

/** The person's Keepo with that id: from the list kept, or asked afresh when it isn't there (made since). */
const loadKeepo = async (id: string): Promise<ApiSiteResponse | undefined> => {
  const kept = findKeepo(await api.getSites(), id);
  if (kept) {
    return kept;
  }
  api.clearCacheSites();
  return findKeepo(await api.getSites(), id);
};

/**
 * A Keepo's screens (/sites/<id>/...): with the Keepo of the address open. The one already open is used as it is;
 * otherwise (a reload, a link) it's opened as the list opens it, with its pending draft if there's one. An address
 * that isn't one of the person's Keepos goes back to the list.
 */
export const SiteRoute = ({ authenticated, site, setSite, children }: Props) => {
  const { siteId: id = "" } = useParams();
  const location = useLocation();
  const { appState, setAppState } = useAppState();
  const { setPage } = usePosLoginContinue();
  const [missing, setMissing] = useState(false);

  const openId = appState.editingSiteId;
  const isOpen = !!site && !!openId && !!findKeepo([{ siteId: openId }], id);

  useEffect(() => {
    if (!authenticated || isOpen) {
      return;
    }
    let current = true;
    setMissing(false);
    loadKeepo(id)
      .then(keepo => {
        if (!current) return;
        const content = keepo?.draft ?? keepo?.site;
        if (!keepo || !content) {
          setMissing(true);
          return;
        }
        setAppState({
          ...appState,
          editingSiteId: keepo.siteId,
          // the pending draft opens as it is, unsaved changes over the published Keepo
          editingSiteModified: !!keepo.draft,
          published: !!keepo.site,
          editingSiteDisabled: !!keepo.disabled,
          notRedirectOnEmptySites: true,
          editingSiteSubscription: keepo.subscription ?? { type: "FREE" },
          editingSiteTrackingIntegrations: keepo.tracking,
          editingSitePasswordProtected: !!keepo.passwordProtected,
          editingSiteAgeGated: !!keepo.ageGate?.enabled,
        });
        setSite(content);
      })
      .catch(() => current && setMissing(true));
    return () => {
      current = false;
    };
    // Once per address: the state set here mustn't load it again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, id, isOpen]);

  // Sign in first, then back here (the whole address)
  useEffect(() => {
    if (!authenticated) {
      setPage(`${location.pathname}${location.search}${location.hash}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  if (!authenticated) {
    return <Navigate to="/" replace />;
  }
  if (missing) {
    return <Navigate to="/sites" replace />;
  }
  if (!isOpen || !site) {
    return <LoadingModal />;
  }
  return <SiteContextProvider value={{ site, setSite }}>{children}</SiteContextProvider>;
};

/** The old addresses of a Keepo's screens (/keepo, /settings...): to the Keepo open, or to the list. */
export const LegacySiteRedirect = () => {
  const { appState } = useAppState();
  const location = useLocation();
  const to = appState.editingSiteId
    ? `${fromLegacyPath(appState.editingSiteId, location.pathname)}${location.search}${location.hash}`
    : "/sites";
  return <Navigate to={to} replace state={location.state} />;
};
